import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { qaApi } from '../../../api/client';
import HelpButton from '../../help/HelpButton';

// 2026-07-16: open the eval-playback in a real popup window so agents
// don't accidentally close the ContinuumCX tab (which would drop their
// softphone registration mid-shift). Same shape used by RecordingsBrowserPage.
function openPopout(recordingId, evalId) {
  const url = `${window.location.origin}/playback/${recordingId}?eval=${evalId}`;
  window.open(
    url,
    `cx-playback-${recordingId}`,
    'width=1400,height=900,menubar=no,toolbar=no,location=no,status=no',
  );
}

function fmtDur(sec) {
  if (sec == null) return '—';
  const s = Math.floor(Number(sec) || 0);
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2,'0')}`;
}

export default function MyAssignments() {
  const [status, setStatus] = useState('draft');

  const { data = [], isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ['qa','my-assignments', status],
    queryFn: () => qaApi.myAssignments({ status_filter: status, limit: 200 }),
    refetchInterval: status === 'draft' ? 30_000 : false,
  });

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-semibold">My QA Assignments</h2>
          <p className="text-xs text-surface-muted">Draft evaluations assigned to you. Click one to open the recording + form.</p>
        </div>
        <div className="flex gap-2 items-center">
          <HelpButton moduleKey="qa_assignments" />
          <select value={status} onChange={(e) => setStatus(e.target.value)}
            className="px-2 py-1 text-sm rounded-thin border border-surface-border dark:border-surface-dark-border bg-surface-base dark:bg-surface-dark-base">
            <option value="draft">Draft (pending)</option>
            <option value="submitted">Submitted</option>
            <option value="all">All</option>
          </select>
          <button onClick={() => refetch()}
            className="px-2 py-1 text-xs rounded-thin border border-surface-border dark:border-surface-dark-border hover:bg-surface-subtle dark:hover:bg-surface-dark-subtle">
            ↻ {isFetching ? '…' : 'Refresh'}
          </button>
        </div>
      </div>

      {isError && (
        <div className="text-sm text-red-600 p-3 rounded-thin border border-red-300/40 mb-3">
          {error?.detail || error?.message || 'Failed to load.'}
        </div>
      )}

      {!isLoading && data.length === 0 && (
        <div className="text-sm text-surface-muted p-3 rounded-thin border border-surface-border dark:border-surface-dark-border">
          No {status === 'all' ? '' : status} assignments right now.
        </div>
      )}

      {data.length > 0 && (
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wider text-surface-muted dark:text-surface-dark-muted">
            <tr>
              <th className="px-2 py-1">Assigned</th>
              <th className="px-2 py-1">Form</th>
              <th className="px-2 py-1">Agent</th>
              <th className="px-2 py-1">Ext</th>
              <th className="px-2 py-1">Queue</th>
              <th className="px-2 py-1">Caller</th>
              <th className="px-2 py-1">Call time</th>
              <th className="px-2 py-1 text-right">Dur</th>
              <th className="px-2 py-1">Status</th>
            </tr>
          </thead>
          <tbody>
            {data.map((r) => (
              <tr key={r.id}
                onClick={() => openPopout(r.recording_id, r.id)}
                title="Opens playback in a new window — keeps your softphone session safe"
                className="border-t border-surface-border dark:border-surface-dark-border cursor-pointer hover:bg-surface-subtle dark:hover:bg-surface-dark-subtle">
                <td className="px-2 py-1.5 text-xs">{new Date(r.created_at).toLocaleString()}</td>
                <td className="px-2 py-1.5">{r.form_name}</td>
                <td className="px-2 py-1.5">{r.agent_name || '—'}</td>
                <td className="px-2 py-1.5 font-mono">{r.agent_extension || '—'}</td>
                <td className="px-2 py-1.5 font-mono">{r.queue_id || '—'}</td>
                <td className="px-2 py-1.5 font-mono">{r.caller_id || '—'}</td>
                <td className="px-2 py-1.5 text-xs">{r.call_started_at ? new Date(r.call_started_at).toLocaleString() : '—'}</td>
                <td className="px-2 py-1.5 text-right font-mono">{fmtDur(r.call_duration_sec)}</td>
                <td className="px-2 py-1.5">
                  <span className={'inline-flex items-center px-2 py-0.5 rounded-full text-xs border ' +
                    (r.status === 'draft' ? 'border-accent-amber/40 text-amber-700 dark:text-amber-300 bg-accent-amber/20'
                     : 'border-accent-green/40 text-green-700 dark:text-green-300 bg-accent-green/20')}>
                    {r.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
